import React from "react";
import styled from "styled-components";

import ComboInput from "../form-item/ComboInput";
import Input from "../form-item/Input";
import MoneyInput from "../form-item/MoneyInput";
import NumberInput from "../form-item/NumberInput";
import IconButton from "../button/IconButton";
// @ts-ignore - JSX コンポーネント（型定義なし）
import ListBlock from "../block/ListBlock";
import type { OfferCompensationLine } from "./types";
import { formatYen } from "../../utils/money";
import { computeCompensationSubtotalExclusive } from "../../utils/offerCompensationLines";
import { CONSUMPTION_TAX_RATE } from "../../utils/tax";

/** よく使う単位の候補（ComboInput の候補表示用）。 */
const UNIT_SUGGESTIONS = ["式", "本", "時間", "日", "ヶ月", "人月", "個"];

/** 行を全削除したときに残す既定行。対価ゼロのオファーを作らせないための保険。 */
export const FALLBACK_COMPENSATION_LINE: OfferCompensationLine = {
  label: "委託費",
  unitPrice: "0",
  quantity: "1",
  unit: "式",
};

const NEW_COMPENSATION_LINE: OfferCompensationLine = {
  label: "",
  unitPrice: "0",
  quantity: "1",
  unit: "式",
};

const GridBase = `
  display: grid;
  grid-template-columns: 1.5fr 1.2fr auto 0.7fr 0.8fr 1.1fr auto;
  gap: 4px;
  align-items: center;
`;

const HeaderRow = styled.div`
  ${GridBase}
  padding: 4px 24px 0px 16px;
  color: var(--soremo-light-gray);
`;

const LineCard = styled.div`
  ${GridBase}
  padding: 4px 4px 4px 4px;
  background: var(--soremo-background, #f7f7f7);
  border-radius: 4px;
`;

const HeaderCell = styled.span.attrs({
  className: "bodytext-11 u-line-height-100",
})``;

const InlineUnit = styled.span.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
  white-space: nowrap;
`;

const SubtotalCell = styled.div.attrs({ className: "bodytext-13" })`
  text-align: right;
  color: #000;
`;

const TotalsTable = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  row-gap: 6px;
  column-gap: 16px;
  padding: 8px 12px 0;
`;

const TotalsLabel = styled.span.attrs({ className: "bodytext-13" })`
  color: var(--soremo-text);
`;

const TotalsValue = styled.span.attrs({ className: "bodytext-13" })`
  text-align: right;
`;

const GrandTotalLabel = styled(TotalsLabel).attrs({
  className: "heading-13",
})``;

const GrandTotalValue = styled(TotalsValue).attrs({
  className: "heading-13",
})``;

const computeLineSubtotal = (line: OfferCompensationLine): number => {
  const unit = Number.parseFloat(line.unitPrice) || 0;
  const quantity = Number.parseFloat(line.quantity) || 0;
  return unit * quantity;
};

interface CompensationLinesEditorProps {
  lines: OfferCompensationLine[];
  onChange: (lines: OfferCompensationLine[]) => void;
}

/**
 * 対価の内訳（`compensation_lines`）の入力テーブル。
 *
 * オファー作成ウィザードの詳細フォーム（`DetailFormStep`）と、締結後の契約更新
 * モーダル（`ContractAmendModal`）で共用する。金額は**税抜・整数円**で保持し、
 * 合計行だけ税込を併記する（`utils/tax.ts` と BE の同期規則に合わせる）。
 *
 * 残高不足の警告はスレッドの状態によって文言が変わるため、このコンポーネントは
 * 持たず呼び出し側が合計値を見て出す。
 */
const CompensationLinesEditor: React.FC<CompensationLinesEditorProps> = ({
  lines,
  onChange,
}) => {
  const subtotalExclTax = computeCompensationSubtotalExclusive(lines);
  const taxAmount = subtotalExclTax * CONSUMPTION_TAX_RATE;
  const totalInclTax = subtotalExclTax + taxAmount;

  const updateLine = (index: number, patch: Partial<OfferCompensationLine>) => {
    onChange(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const addLine = () => {
    onChange([...lines, { ...NEW_COMPENSATION_LINE }]);
  };

  const removeLine = (index: number) => {
    const next = lines.filter((_, i) => i !== index);
    // 全削除を防ぎ、最低 1 行は残す
    onChange(next.length > 0 ? next : [{ ...FALLBACK_COMPENSATION_LINE }]);
  };

  return (
    <>
      {lines.length > 0 && (
        <HeaderRow>
          <HeaderCell>項目</HeaderCell>
          <HeaderCell>単価（税抜）</HeaderCell>
          <span />
          <HeaderCell>数量</HeaderCell>
          <HeaderCell>単位</HeaderCell>
          <HeaderCell style={{ textAlign: "right" }}>小計</HeaderCell>
          <span />
        </HeaderRow>
      )}

      {lines.map((line, idx) => (
        <LineCard key={idx}>
          <Input
            placeholder="項目"
            value={line.label}
            onChange={(e) => updateLine(idx, { label: e.target.value })}
          />
          <MoneyInput
            placeholder="単価"
            suffix=""
            value={line.unitPrice}
            onChange={(e) => updateLine(idx, { unitPrice: e.target.value })}
          />
          <InlineUnit>円 ×</InlineUnit>
          <NumberInput
            placeholder="数量"
            value={line.quantity}
            onChange={(e) => updateLine(idx, { quantity: e.target.value })}
            allowDecimal
          />
          <ComboInput
            placeholder="単位"
            value={line.unit}
            onChange={(e) => updateLine(idx, { unit: e.target.value })}
            options={UNIT_SUGGESTIONS}
          />
          <SubtotalCell>= {formatYen(computeLineSubtotal(line))}</SubtotalCell>
          <IconButton
            style={{ fontSize: 20 }}
            onClick={() => removeLine(idx)}
            icon="delete_outline"
          />
        </LineCard>
      ))}

      <ListBlock type="add" label="項目を追加" onClick={addLine} />

      <TotalsTable>
        <TotalsLabel>課税対象額</TotalsLabel>
        <TotalsValue>{formatYen(subtotalExclTax)}</TotalsValue>
        <TotalsLabel>
          消費税（{Math.round(CONSUMPTION_TAX_RATE * 100)}%）
        </TotalsLabel>
        <TotalsValue>{formatYen(taxAmount)}</TotalsValue>
        <GrandTotalLabel>合計</GrandTotalLabel>
        <GrandTotalValue>{formatYen(totalInclTax)}</GrandTotalValue>
      </TotalsTable>
    </>
  );
};

export default CompensationLinesEditor;
